import { ChevronRightIcon, Gem } from 'lucide-react';

import { useState } from 'react';

import { EXAMPLE_GROUP_ID, groupProEntry, userPro } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { Card } from '@splitfiesta/ui';

import { ProGroupDialogContainer } from '@/components/pro/ProGroupDialogContainer';
import { ProPill } from '@/components/pro/ProPill';
import { GroupSettingsRowSkeleton } from '@/components/skeleton';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import {
  useGroupQuery,
  useGroupWithDashboardQuery,
} from '@/hooks/queries/useGroups';

import { useAuthStore } from '@/stores/authStore';

interface GroupProSettingsSectionProps {
  groupId: string;
}

/**
 * Group settings' way into the Pro dialog, the twin of the header's
 * "Make Pro" button and Pro pill. Renders nothing when Pro is not offered.
 */
export function GroupProSettingsSection({
  groupId,
}: GroupProSettingsSectionProps) {
  const { t } = useTranslation();
  const proEnabled = useFeatureFlag('pro');
  const user = useAuthStore(state => state.user);
  const [isOpen, setIsOpen] = useState(false);
  // The dialog names the supporters from the dashboard's balances, which the
  // edit page's plain group fetch leaves out.
  const enabled = proEnabled && groupId !== EXAMPLE_GROUP_ID;
  const { data: group, isLoading } = useGroupWithDashboardQuery(
    groupId,
    enabled
  );
  // Already cached by the edit form, so this costs no request.
  const { data: plainGroup } = useGroupQuery(groupId, enabled);
  const cardMayShow = userPro(user).active || !!plainGroup?.pro?.active;

  if (!group) {
    return isLoading && cardMayShow ? (
      <GroupSettingsRowSkeleton data-testid="group-settings-pro-skeleton" />
    ) : null;
  }
  const { isPro, canMakePro } = groupProEntry({ proEnabled, user, group });
  if (!isPro && !canMakePro) return null;

  return (
    <Card className="mb-6">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={t(isPro ? 'groups:pro.pillA11y' : 'groups:pro.actionA11y')}
        data-testid={
          isPro ? 'group-settings-pro-pill' : 'group-settings-make-pro'
        }
        className="flex w-full min-h-11 items-center gap-3 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
      >
        {isPro ? (
          <ProPill size="md" />
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2 font-semibold text-orange-600 dark:text-orange-400">
            <Gem aria-hidden="true" className="h-5 w-5 shrink-0" />
            {t('groups:pro.action')}
          </span>
        )}
        <ChevronRightIcon
          aria-hidden="true"
          className="ml-auto h-5 w-5 shrink-0 text-gray-400"
        />
      </button>
      <ProGroupDialogContainer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        group={group}
      />
    </Card>
  );
}
